// src/components/WebviewAuthHandoff.js
//
// Consumes an `authToken` URL query param and signs the user in via
// Firebase's signInWithCustomToken, then strips the token from the URL
// so it doesn't linger in browser history.
//
// The token comes from the WeChat Mini Program: when a user taps an
// event card there, the Mini Program fetches a short-lived Firebase
// custom token (miniProgramGetHandoffToken Cloud Function) and appends
// it as ?authToken=... on the URL it opens inside <web-view>. This
// component's job is to swap that token for a real session on page
// load, so the user lands already signed in and can register in one
// tap — no re-login inside the web-view.
//
// Renders nothing. Mount it once, high in the tree (inside
// AuthProvider so onAuthStateChanged fires normally afterward).
//
// Non-blocking: if the token is missing, expired, or the sign-in call
// fails for any reason, we log and move on. The page still renders;
// the user just sees the normal login prompt if they try to register.
// This is intentional — we don't want a malformed URL param to break
// the entire site.

import { useEffect, useRef } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase';

export default function WebviewAuthHandoff() {
  // React StrictMode double-invokes effects in dev, and custom tokens
  // are single-use — a second signInWithCustomToken() call with the
  // same token throws "TOKEN_ALREADY_USED" and the console fills with
  // scary errors. This ref makes sure we only run once per mount.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('authToken');
    if (!token) return; // Not a handoff visit — normal page load.

    ran.current = true;

    // Strip the token from the URL FIRST, before the async sign-in
    // resolves. Two reasons:
    //   1. If the user hits refresh mid-signin, the (now consumed)
    //      token isn't in the URL to be tried again and fail.
    //   2. The token doesn't sit in the address bar / share sheet /
    //      browser history where it could be copied.
    // replaceState (not pushState) so there's no extra back-button entry.
    params.delete('authToken');
    const search = params.toString();
    const cleanUrl =
      window.location.pathname +
      (search ? '?' + search : '') +
      window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    signInWithCustomToken(auth, token)
      .then(() => {
        // AuthContext's onAuthStateChanged picks it up from here — no
        // navigation needed, the current page just re-renders with a
        // signed-in user.
        console.log('[WebviewAuthHandoff] signed in via Mini Program handoff');
      })
      .catch((err) => {
        // Most likely causes: token expired (>1h since mint), token
        // was already consumed by a previous mount, or Firebase Auth
        // is misconfigured. Log it but don't disrupt the page — the
        // user can still sign in manually if they want to register.
        console.warn('[WebviewAuthHandoff] handoff sign-in failed:', err.code || err.message);
      });
  }, []);

  return null;
}
